
































import { Component, Vue, Prop } from 'vue-property-decorator';

import SectionData from '@/componentsV2/organisms/SectionData/SectionData.vue';
import CheckboxLegend from '@/componentsV2/molecules/Chart/CheckboxLegend.vue';
import BaseChart from '@/componentsV2/molecules/Chart/BaseChart.vue';
import { AnalyticsCardListItem } from '@/componentsV2/organisms/AnalyticsCardList/AnalyticsCardList.vue';

import PerformanceOverviewService from '@/api/PerformanceOverview';

import { IPeriod } from '@/models/period';
import { IChartSerie } from '@/models/performance';

import PerformanceUtils, { DatePickerShortcut } from '@/utils/PerformanceUtils';
import { EAnalyticsCardArrowDirection } from '@/componentsV2/molecules/AnalyticsCard/AnalyticsCard.vue';

const analyticsMapperBySlug: Record<string, any> = {
  'new-customers': {
    key: 'newCustomers'
  },
  'returning-customers': {
    key: 'returningCustomers'
  },
  transactions: {
    key: 'transactions'
  }
};

@Component({
  components: {
    SectionData,
    CheckboxLegend,
    BaseChart
  }
})
export default class PerformanceDailyMemberGrowth extends Vue {
  @Prop() gaEventPage!: string;

  performanceOverviewService = new PerformanceOverviewService();
  period: IPeriod = PerformanceUtils.defaultPeriod();
  options = PerformanceUtils.chartOptions({
    colors: ['#D300F5', '#00BABA', '#B3B3B3']
  });
  series: IChartSerie[] = [];
  loading = false;

  analytics: Record<string, AnalyticsCardListItem> = {
    newCustomers: {
      hidden: true,
      insightVariant: 'normal',
      infos: {
        amount: '0',
        description: 'New members acquired in<br />the last period',
        insightText: '',
        tooltipText: ''
      }
    },
    returningCustomers: {
      hidden: true,
      insightVariant: 'normal',
      infos: {
        amount: '0',
        description: 'Returning members in the<br />last period',
        insightText: '',
        tooltipText: ''
      }
    },
    transactions: {
      hidden: true,
      insightVariant: 'normal',
      infos: {
        amount: '0',
        description: 'Transactions from the last<br />period',
        insightText: '',
        tooltipText: ''
      }
    }
  };

  get legendGATracking() {
    return {
      eventCategory: `${this.gaEventPage} | Customer Growth chart - Legend`,
      eventAction: 'click'
    };
  }

  get gaTracking() {
    return {
      period: {
        eventCategory: `${this.gaEventPage} | Customer Growth chart - Period`,
        eventAction: 'change'
      }
    };
  }

  get translatedAnalytics() {
    return Object.entries(this.analytics)
      .map(([key, value]) => ({
        ...value,
        infos: {
          ...value.infos,
          tooltipText: this.$t(`${this.tPrefix}.analytics.${key}.tooltip`)
        }
      }))
      .filter(({ hidden }) => !hidden);
  }

  get queryParameters() {
    return {
      from: this.period.start,
      to: this.period.end,
      ...(this.period.shortcut && {
        slug:
          PerformanceUtils.datepickerShortcutMapper[
            this.period.shortcut as DatePickerShortcut
          ]
      })
    };
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessState'];
  }

  get subtitle() {
    return this.$t(`${this.tPrefix}.subtitle.${this.businessInfo.reportType}`);
  }

  get tPrefix() {
    return 'performance.overview.memberGrowth';
  }

  created() {
    this.getCustomerGrowth();
  }

  async getCustomerGrowth({ useFilters = true } = {}) {
    this.loading = true;

    const queryParameters = useFilters ? this.queryParameters : {};

    const response = await this.performanceOverviewService.getCustomerGrowth(
      queryParameters
    );

    this.loading = false;

    if (response.status !== 200) return;

    for (const dataset of response.data.datasets) {
      if (!analyticsMapperBySlug[dataset.slug]) continue;

      const slugKey = analyticsMapperBySlug[dataset.slug].key;

      const datasetPercentage = Number(dataset.percentage || 0);
      const roundedDatasetPercentage = Math.round(datasetPercentage);

      this.analytics = {
        ...this.analytics,
        [slugKey]: {
          ...this.analytics[slugKey],
          insightVariant:
            roundedDatasetPercentage > 0
              ? 'success'
              : roundedDatasetPercentage < 0
              ? 'danger'
              : 'normal',
          hidden: false,
          infos: {
            ...this.analytics[slugKey].infos,
            amount: dataset.total,
            arrowDirection:
              roundedDatasetPercentage > 0
                ? EAnalyticsCardArrowDirection.UP
                : roundedDatasetPercentage < 0
                ? EAnalyticsCardArrowDirection.DOWN
                : undefined,
            insightText: this.period.shortcut
              ? `${roundedDatasetPercentage}% ${PerformanceUtils.getComparativeLabel(
                  this.period.shortcut as DatePickerShortcut
                )}`
              : ''
          }
        }
      };
    }

    this.series = PerformanceUtils.normalizedChartSeries(response.data);
  }

  handlePeriodChange(period: IPeriod) {
    this.period = PerformanceUtils.normalizedPeriod(period);
    this.getCustomerGrowth();
  }
}
